<template>
  <ContentWrap title="实体房间管理">
    <!-- 搜索工作栏 -->
    <el-form :model="queryParams" ref="queryFormRef" :inline="true" label-width="68px" class="-mb-15px">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="queryParams.shopId"
          placeholder="请选择店铺"
          clearable
          class="!w-160px"
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房型" prop="spuId">
        <el-select
          v-model="queryParams.spuId"
          placeholder="请选择房型"
          clearable
          class="!w-160px"
        >
          <el-option
            v-for="spu in spuList"
            :key="spu.id"
            :label="spu.name"
            :value="spu.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房间号" prop="roomNo">
        <el-input
          v-model="queryParams.roomNo"
          placeholder="请输入房间号"
          clearable
          class="!w-160px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="房间状态" prop="roomState">
        <el-select
          v-model="queryParams.roomState"
          placeholder="请选择状态"
          clearable
          class="!w-160px"
        >
          <el-option label="空闲" :value="0" />
          <el-option label="预订" :value="1" />
          <el-option label="入住" :value="2" />
          <el-option label="退房" :value="3" />
          <el-option label="清洁" :value="4" />
          <el-option label="维修" :value="5" />
          <el-option label="锁定" :value="6" />
        </el-select>
      </el-form-item>
      <el-form-item label="楼层" prop="floor">
        <el-input
          v-model="queryParams.floor"
          placeholder="请输入楼层"
          clearable
          class="!w-160px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" /> 搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" /> 重置</el-button>
        <el-button
          type="primary"
          plain
          @click="openForm('create')"
          v-hasPermi="['hotel:room:create']"
        >
          <Icon icon="ep:plus" class="mr-5px" /> 新增
        </el-button>
        <el-button
          type="warning"
          plain
          @click="openBatchCreateDialog"
          v-hasPermi="['hotel:room:create']"
        >
          <Icon icon="ep:copy-document" class="mr-5px" /> 批量创建
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 房间列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true">
      <el-table-column label="房间号" align="center" prop="roomNo" min-width="120" />
      <el-table-column label="楼层" align="center" prop="floor" width="100">
        <template #default="scope">
          <span>{{ scope.row.floor || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="房型" align="center" prop="spuId" min-width="120">
        <template #default="scope">
          <el-tag v-if="scope.row.spuName" type="info">{{ scope.row.spuName }}</el-tag>
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
      <el-table-column label="房间状态" align="center" prop="roomState" width="120">
        <template #default="scope">
          <el-tag :type="getRoomStateTag(scope.row.roomState)">
            {{ getRoomStateLabel(scope.row.roomState) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="备注" align="center" prop="remark" min-width="150">
        <template #default="scope">
          <span>{{ scope.row.remark || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" align="center" prop="createTime" width="180">
        <template #default="scope">
          <span>{{ scope.row.createTime }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="160">
        <template #default="scope">
          <el-button
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
            v-hasPermi="['hotel:room:update']"
          >
            编辑
          </el-button>
          <el-button
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
            v-hasPermi="['hotel:room:delete']"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 新增/编辑弹窗 -->
  <Dialog :title="dialogTitle" v-model="dialogVisible" width="500px">
    <el-form ref="formRef" :model="formData" :rules="formRules" label-width="100px" v-loading="formLoading">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="formData.shopId"
          placeholder="请选择店铺"
          clearable
          filterable
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房型" prop="spuId">
        <el-select
          v-model="formData.spuId"
          placeholder="请选择房型"
          clearable
          filterable
        >
          <el-option
            v-for="spu in spuList"
            :key="spu.id"
            :label="spu.name"
            :value="spu.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房间号" prop="roomNo">
        <el-input v-model="formData.roomNo" placeholder="请输入房间号" />
      </el-form-item>
      <el-form-item label="楼层" prop="floor">
        <el-input v-model="formData.floor" placeholder="请输入楼层" />
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input v-model="formData.remark" type="textarea" placeholder="请输入备注" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitForm" type="primary" :disabled="formLoading">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>

  <!-- 批量创建弹窗 -->
  <Dialog :title="'批量创建房间'" v-model="batchDialogVisible" width="600px">
    <el-form ref="batchFormRef" :model="batchFormData" :rules="batchFormRules" label-width="110px" v-loading="batchFormLoading">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="batchFormData.shopId"
          placeholder="请选择店铺"
          clearable
          filterable
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房型" prop="spuId">
        <el-select
          v-model="batchFormData.spuId"
          placeholder="请选择房型"
          clearable
          filterable
        >
          <el-option
            v-for="spu in spuList"
            :key="spu.id"
            :label="spu.name"
            :value="spu.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="楼层" prop="floor">
        <el-input v-model="batchFormData.floor" placeholder="请输入楼层" />
      </el-form-item>
      <el-form-item label="房号前缀" prop="prefix">
        <el-input v-model="batchFormData.prefix" placeholder="可选：如 A" />
      </el-form-item>
      <el-form-item label="起始房号" prop="startRoomNo">
        <el-input v-model="batchFormData.startRoomNo" placeholder="请输入起始房号" />
      </el-form-item>
      <el-form-item label="结束房号" prop="endRoomNo">
        <el-input v-model="batchFormData.endRoomNo" placeholder="请输入结束房号" />
      </el-form-item>
      <el-form-item label="房号后缀" prop="suffix">
        <el-input v-model="batchFormData.suffix" placeholder="可选：如 -01" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitBatchForm" type="primary" :disabled="batchFormLoading">确 定</el-button>
      <el-button @click="batchDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as roomApi from '@/api/hotel/room'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import { getSpuSimpleList } from '@/api/mall/product/spu'

defineOptions({ name: 'HotelRoom' })

const message = useMessage()

const loading = ref(true)
const shopList = ref<{ id: string | number; name: string }[]>([])
const spuList = ref<{ id: string | number; name: string }[]>([])

const fetchDropdownData = async () => {
  try {
    const [shopRes, spuRes] = await Promise.all([
      getShopSimpleList(),
      getSpuSimpleList()
    ])
    shopList.value = shopRes || []
    spuList.value = spuRes || []
  } catch {
    // 静默处理
  }
}
const list = ref<roomApi.RoomVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  shopId: undefined,
  spuId: undefined,
  roomNo: undefined,
  roomState: undefined,
  floor: undefined
})
const queryFormRef = ref()

const roomStateMap: Record<number, string> = {
  0: '空闲',
  1: '预订',
  2: '入住',
  3: '退房',
  4: '清洁',
  5: '维修',
  6: '锁定'
}

const roomStateTagMap: Record<number, string> = {
  0: 'success',
  1: 'warning',
  2: 'primary',
  3: 'info',
  4: '',
  5: 'danger',
  6: 'danger'
}

const getRoomStateLabel = (state: number) => roomStateMap[state] || '未知'
const getRoomStateTag = (state: number) => roomStateTagMap[state] || 'info'

const getList = async () => {
  loading.value = true
  try {
    const data = await roomApi.getRoomPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

// 新增/编辑弹窗
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formLoading = ref(false)
const formType = ref('')
const formRef = ref()
const formData = ref({
  id: undefined,
  shopId: undefined,
  spuId: undefined,
  roomNo: '',
  floor: '',
  remark: ''
})
const formRules = reactive({
  shopId: [{ required: true, message: '请选择店铺', trigger: 'change' }],
  spuId: [{ required: true, message: '请选择房型', trigger: 'change' }],
  roomNo: [{ required: true, message: '请输入房间号', trigger: 'blur' }]
})

const openForm = async (type: string, id?: string | number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增房间' : '编辑房间'
  formType.value = type
  formData.value = {
    id: undefined,
    shopId: undefined,
    spuId: undefined,
    roomNo: '',
    floor: '',
    remark: ''
  }
  formRef.value?.resetFields()
  if (id) {
    formLoading.value = true
    try {
      const data = await roomApi.getRoom(id)
      formData.value = { ...data }
    } finally {
      formLoading.value = false
    }
  }
}

const submitForm = async () => {
  await formRef.value.validate()
  formLoading.value = true
  try {
    const data = { ...formData.value } as any
    // 转换数字字段
    if (formType.value === 'create') {
      await roomApi.createRoom(data)
      message.success('新增成功')
    } else {
      await roomApi.updateRoom(data)
      message.success('修改成功')
    }
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

const handleDelete = async (id: string | number) => {
  try {
    await message.delConfirm()
    await roomApi.deleteRoom(id)
    message.success('删除成功')
    await getList()
  } catch {}
}

// 批量创建
const batchDialogVisible = ref(false)
const batchFormLoading = ref(false)
const batchFormRef = ref()
const batchFormData = reactive({
  shopId: undefined as string | number | undefined,
  spuId: undefined as string | number | undefined,
  floor: '',
  prefix: '',
  startRoomNo: '',
  endRoomNo: '',
  suffix: ''
})
const batchFormRules = reactive({
  shopId: [{ required: true, message: '请选择店铺', trigger: 'change' }],
  spuId: [{ required: true, message: '请选择房型', trigger: 'change' }],
  floor: [{ required: true, message: '请输入楼层', trigger: 'blur' }],
  startRoomNo: [{ required: true, message: '请输入起始房号', trigger: 'blur' }],
  endRoomNo: [{ required: true, message: '请输入结束房号', trigger: 'blur' }]
})

const openBatchCreateDialog = () => {
  batchFormData.shopId = undefined
  batchFormData.spuId = undefined
  batchFormData.floor = ''
  batchFormData.prefix = ''
  batchFormData.startRoomNo = ''
  batchFormData.endRoomNo = ''
  batchFormData.suffix = ''
  batchFormRef.value?.resetFields()
  batchDialogVisible.value = true
}

const submitBatchForm = async () => {
  await batchFormRef.value.validate()
  batchFormLoading.value = true
  try {
    const roomList = generateRoomList()
    if (roomList.length === 0) return
    await roomApi.batchCreate(roomList)
    message.success('批量创建成功')
    batchDialogVisible.value = false
    await getList()
  } finally {
    batchFormLoading.value = false
  }
}

const generateRoomList = (): roomApi.RoomVO[] => {
  // 解析起始房号：前缀 + 数字 + 后缀
  const startMatch = batchFormData.startRoomNo.match(/^(\D*)(\d+)(\D*)$/)
  if (!startMatch) {
    message.error('起始房号格式不正确')
    return []
  }
  const startPrefix = startMatch[1]
  const startNum = parseInt(startMatch[2])
  const startSuffix = startMatch[3]
  const startNumStr = startMatch[2]

  // 解析结束房号
  const endMatch = batchFormData.endRoomNo.match(/^(\D*)(\d+)(\D*)$/)
  if (!endMatch) {
    message.error('结束房号格式不正确')
    return []
  }
  const endNum = parseInt(endMatch[2])

  if (isNaN(startNum) || isNaN(endNum) || startNum > endNum) {
    message.error('结束房号不能小于起始房号')
    return []
  }

  // 优先使用表单里的前缀/后缀；如果表单为空，从起始房号里提取
  const prefix = batchFormData.prefix || startPrefix
  const suffix = batchFormData.suffix || startSuffix
  const length = startNumStr.length

  const list: roomApi.RoomVO[] = []
  for (let i = startNum; i <= endNum; i++) {
    const numStr = i.toString().padStart(length, '0')
    list.push({
      shopId: batchFormData.shopId!,
      spuId: batchFormData.spuId!,
      roomNo: `${prefix}${numStr}${suffix}`,
      floor: batchFormData.floor,
      roomState: 0,
      remark: ''
    })
  }
  return list
}

onMounted(() => {
  fetchDropdownData()
  getList()
})
</script>
